import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import type { Result } from '@/lib/http/HttpClient';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { ReferencePreview } from '../utils/transformToolsData';
import { ToolsHierarchy } from './ToolsHierarchy';
import { ToolsTable } from './ToolsTable';
import type { ToolTableRow } from './ToolsTable';
import { useTranslation } from '@/hooks/useTranslation';

export type { ToolTableRow };

interface ToolsListProps {
  // Data
  isLoading: boolean;
  apiResult: Result<AllToolsResponse> | null;
  hierarchy: ReferencePreview[];
  tableRows: ToolTableRow[];
  totalReferences: number;
  expandedReferences: Set<string>;

  // Pagination
  hasMoreResults?: boolean;
  onShowMore?: () => void;

  // Control state
  allExpanded: boolean;
  anyExpanded: boolean;

  // Callbacks
  onToggleReference: (referenceId: string) => void;
  onNavigateToTool: (toolId: string) => void;
  onRetry: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

export function ToolsList({
  isLoading,
  apiResult,
  hierarchy,
  tableRows,
  totalReferences,
  expandedReferences,
  hasMoreResults,
  onShowMore,
  allExpanded,
  anyExpanded,
  onToggleReference,
  onNavigateToTool,
  onRetry,
  onExpandAll,
  onCollapseAll,
}: ToolsListProps) {
  const { t } = useTranslation();
  return (
    <Tabs defaultValue="hierarchy" className="w-full">
      <Card
        padding="none"
        className="mb-3 flex flex-col items-start gap-3 p-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <TabsList>
          <TabsTrigger value="hierarchy">{t('toolView.list.tabs.hierarchy')}</TabsTrigger>
          <TabsTrigger value="table">{t('toolView.list.tabs.table')}</TabsTrigger>
        </TabsList>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onExpandAll}
            disabled={allExpanded}
            aria-label={t('toolView.list.actions.expandAll')}
            title={t('toolView.list.actions.expandAll')}
            className="gap-2"
          >
            <ChevronsUpDown className="size-4" aria-hidden />
            {t('toolView.list.actions.expandAll')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCollapseAll}
            disabled={!anyExpanded}
            aria-label={t('toolView.list.actions.collapseAll')}
            title={t('toolView.list.actions.collapseAll')}
            className="gap-2"
          >
            <ChevronsDownUp className="size-4" aria-hidden />
            {t('toolView.list.actions.collapseAll')}
          </Button>
        </div>
      </Card>

      <TabsContent value="hierarchy" className="space-y-4">
        <ToolsHierarchy
          isLoading={isLoading}
          apiResult={apiResult}
          hierarchy={hierarchy}
          expandedReferences={expandedReferences}
          onToggleReference={onToggleReference}
          onNavigateToTool={onNavigateToTool}
          onRetry={onRetry}
        />

        {hasMoreResults && !isLoading && onShowMore && (
          <div className="flex justify-center pt-6">
            <Button variant="outline" onClick={onShowMore}>
              {t('toolView.list.actions.showMore')}
            </Button>
          </div>
        )}
      </TabsContent>

      <TabsContent value="table" className="space-y-4">
        <ToolsTable
          isLoading={isLoading}
          apiResult={apiResult}
          tableRows={tableRows}
          totalReferences={totalReferences}
          onRetry={onRetry}
        />

        {hasMoreResults && !isLoading && onShowMore && (
          <div className="flex justify-center pt-6">
            <Button variant="outline" onClick={onShowMore}>
              {t('toolView.list.actions.showMore')}
            </Button>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
